import { useCallback } from 'react';
import { usePersistedDataStore, useLocalDataStore } from '../../../store';
import { useUser } from '../../../UserContext';
import { hasAnyUnsentAventineOffers, getUnsentAventineOfferCaseSummaries } from '../utils/helpers';
import logger from '../../../../utils/logger';

let pendingNavigation = null;

const SUMMARY_TABLE_REPORT_TYPES = new Set(['standard', undefined, null, '']);

export const shouldGuardSummaryTableLeave = ({
  townView,
  reviewPage,
  negotiationObj,
}) => {
  if (townView) return false;
  if (!reviewPage) return false;
  const reportType = negotiationObj?.reportType;
  if (reportType && !SUMMARY_TABLE_REPORT_TYPES.has(reportType)) {
    return false;
  }
  return hasAnyUnsentAventineOffers(negotiationObj);
};

export const useUnsentOffersNavigationGuard = () => {
  const { townView } = useUser();
  const getReviewPage = useLocalDataStore((state) => state.reviewPage);
  const getNegotiationObj = usePersistedDataStore((state) => state.negotiationObj);
  const dialogOpen = useLocalDataStore((state) => state.unsentOffersLeaveDialogOpen);
  const setDialogOpen = useLocalDataStore((state) => state.setUnsentOffersLeaveDialogOpen);

  const shouldGuard = useCallback(
    () =>
      shouldGuardSummaryTableLeave({
        townView,
        reviewPage: getReviewPage,
        negotiationObj: getNegotiationObj,
      }),
    [townView, getReviewPage, getNegotiationObj],
  );

  const guardNavigation = useCallback(
    (action) => {
      if (!shouldGuard()) {
        action();
        return;
      }
      logger.warn('[UNSENT_OFFERS_GUARD] navigation blocked', {
        muniCode: getNegotiationObj?.MuniCode,
        taxYear: getNegotiationObj?.TaxYear,
        cases: getUnsentAventineOfferCaseSummaries(getNegotiationObj),
      });
      pendingNavigation = action;
      setDialogOpen(true);
    },
    [shouldGuard, setDialogOpen, getNegotiationObj],
  );

  const confirmLeave = useCallback(() => {
    setDialogOpen(false);
    const action = pendingNavigation;
    pendingNavigation = null;
    action?.();
  }, [setDialogOpen]);

  const cancelLeave = useCallback(() => {
    setDialogOpen(false);
    pendingNavigation = null;
  }, [setDialogOpen]);

  return {
    dialogOpen,
    guardNavigation,
    confirmLeave,
    cancelLeave,
    shouldGuard,
  };
};
